import React, { ReactNode, useEffect, useState } from 'react';
import { get } from 'lodash';
import { startOfDay, add } from 'date-fns';
import format from 'date-fns/format';

import {
  IconButton,
  Paper,
  Slider,
  SliderProps,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { fontFamily } from '../theme/typography';

import { variablesChange } from '../store/actions';
import { VariableId } from '../store/types';
import { useDispatch, useSelector } from '../store/utils';
import { useVisibility, VisibilityProps } from '../store/use-visibility';

import { useFormatNumber, FormatNumberOpts } from '../utils/format-number';
import { CurrencyRangeValue, sliderStepCurrency } from '../utils/currencies';

type Type = 'numeric' | 'time' | 'minutes';

type BaseProps = {
  type?: Type;
  title: ReactNode;
  subtitle?: ReactNode;
  variableId: VariableId;
  track?: SliderProps['track'];
} & VisibilityProps;

/** When opts.style === 'currency', min/max/step must specify a value per currency. */
type CurrencySliderProps = BaseProps & {
  opts: FormatNumberOpts & { style: 'currency' };
  min?: CurrencyRangeValue;
  max?: CurrencyRangeValue;
  step?: CurrencyRangeValue;
};

type NonCurrencySliderProps = BaseProps & {
  opts?: FormatNumberOpts & {
    style?: Exclude<'decimal' | 'currency' | 'percent' | 'unit', 'currency'>;
  };
} & Pick<SliderProps, 'min' | 'max' | 'step'>;

type Props = CurrencySliderProps | NonCurrencySliderProps;

export const sliderSx: SxProps<Theme> = {
  mb: 1,
  pointerEvents: 'none',
  '& .MuiSlider-track': {
    border: 1,
    borderRadius: 0,
  },
  '& .MuiSlider-rail': {
    borderRadius: 0,
  },
  '&:not(.MuiSlider-trackInverted) .MuiSlider-rail': {
    bgcolor: (theme) =>
      theme.palette.mode === 'light' ? '#fbfcfe' : '#39393D',
    opacity: 1,
  },
  '&.MuiSlider-trackInverted .MuiSlider-track': {
    borderColor: (theme) =>
      theme.palette.mode === 'light' ? '#fbfcfe' : '#39393D',
    bgcolor: (theme) =>
      theme.palette.mode === 'light' ? '#fbfcfe' : '#39393D',
  },
  '&.MuiSlider-trackInverted .MuiSlider-rail': {
    border: 1,
  },
  '& .MuiSlider-thumb': {
    boxShadow: 'none',
    width: 18,
    height: 18,
    pointerEvents: 'all',
    '&:before': {
      boxShadow: 'none',
      width: 9,
      height: 9,
      bgcolor: 'common.white',
    },
  },
};

const buttonSx: SxProps = {
  width: 28,
  height: 28,
  borderColor: 'text.disabled',
  borderWidth: 0.5,
  borderStyle: 'solid',
  borderRadius: 1,
  p: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.6,
  fontFamily,
};

export const SliderBox = ({
  type = 'numeric',
  title,
  subtitle,
  variableId,
  opts,
  visibility,
  min: minProp,
  max: maxProp,
  step: stepProp,
  track,
}: Props) => {
  const formatNumber = useFormatNumber();

  const value = useSelector((s) => get(s.variables, variableId) as number);
  const currency = useSelector(({ variables: { CURRENCY } }) => CURRENCY);
  const dispatch = useDispatch();

  const [localValue, setLocalValue] = useState(value);

  const getNumericStep = () => {
    if (opts?.style === 'currency') {
      return sliderStepCurrency[currency];
    }
    return undefined;
  };

  // Resolves a per-currency range prop (or plain number for non-currency sliders).
  const resolveProp = (
    prop: CurrencyRangeValue | number | null | undefined
  ): number | undefined => {
    if (prop == null) return undefined;
    if (opts?.style === 'currency')
      return (prop as CurrencyRangeValue)[currency];
    return prop as number;
  };

  const typeMap: Record<
    Type,
    {
      formatted: string;
      min: number | undefined;
      max: number | undefined;
      step: number | undefined;
    }
  > = {
    numeric: {
      formatted: formatNumber(localValue, opts),
      min: resolveProp(minProp),
      max: resolveProp(maxProp),
      step: resolveProp(stepProp) ?? getNumericStep(),
    },
    time: {
      formatted: format(
        add(startOfDay(new Date()), { seconds: localValue }),
        'HH:mm'
      ),
      min: minProp as number | undefined,
      max: (maxProp as number | undefined) ?? 86_100,
      step: (stepProp as number | null | undefined) || 5 * 60,
    },
    minutes: {
      formatted: `${Math.floor(localValue / 60)} min`,
      min: minProp as number | undefined,
      max: maxProp as number | undefined,
      step: (stepProp as number | null | undefined) || undefined,
    },
  };

  const localFormattedValue = typeMap[type].formatted;
  const min = typeMap[type].min;
  const max = typeMap[type].max;
  const step = typeMap[type].step ?? 1;

  const handleChange = (v: number) => {
    dispatch(variablesChange({ [variableId]: v }));
  };

  const onChangeCommitted = (v: number) => {
    handleChange(v);
  };

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const decrease = () => {
    const minForButton = typeof min === 'number' ? min : 0;
    const newValue = value - step;
    if (newValue < minForButton) return;
    handleChange(newValue);
  };
  const increase = () => {
    const maxForButton = typeof max === 'number' ? max : 100;
    const newValue = value + step;
    if (newValue > maxForButton) return;
    handleChange(newValue);
  };

  if (!useVisibility(visibility)) return null;

  return (
    <Paper>
      {/* title & value */}
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h3">{title}</Typography>
        <Typography variant="h3">{localFormattedValue}</Typography>
      </Stack>

      {/* subtitle */}
      {subtitle && (
        <Typography variant="h5" sx={{ color: 'primary.main' }}>
          {subtitle}
        </Typography>
      )}

      {/* slider */}
      <Slider
        value={localValue}
        onChange={(e, v) => setLocalValue(v as number)}
        onChangeCommitted={(e, v) => onChangeCommitted(v as number)}
        sx={sliderSx}
        track={track}
        step={step}
        min={min}
        max={max}
      />

      {/* controls */}
      <Stack direction="row" justifyContent="space-between">
        <IconButton sx={buttonSx} onClick={decrease}>
          -
        </IconButton>
        <IconButton sx={buttonSx} onClick={increase}>
          +
        </IconButton>
      </Stack>
    </Paper>
  );
};
